import { useIsAuthenticated, useLoggedInMember } from "@/modules/auth/states";
import { getZaniaTeamMemberRoles } from "@/modules/team/states";
import ZaniaLogoLoader from "@/shared/components/page-loader";
import { ROUTES } from "@/shared/constants/routes";
import { useFeatureAccess } from "@/shared/hooks/use-feature-access";
import { Navigate } from "react-router-dom";
import { mainSidebarItems } from "../constants/sidebar";

/**
 * DefaultRedirect Component
 *
 * Redirects users to the first accessible sidebar item based on their permissions.
 * Used as the catch-all route to ensure users land on a page they have access to.
 *
 * Priority order (based on mainSidebarItems):
 * 1. Compliance → /compliance/gap-assessments
 * 2. Third-Party Risk → /third-party-risk/home
 * 3. Internal Risk → /internal-risk/risk-assessments
 * 4. Ask Zania → /ask-ai
 * 5. Settings (fallback)
 */
export default function DefaultRedirect() {
  const loggedInMember = useLoggedInMember();
  const isAuthenticated = useIsAuthenticated();

  // Get feature access for items that use feature flags
  // NOTE: Hooks must be called before any conditional returns
  const featureNames = mainSidebarItems
    .map((item) => item.featureName)
    .filter((name): name is string => name !== undefined);
  const featureAccess = useFeatureAccess({ features: featureNames });

  // Wait for member data when authenticated (will be fetched by App.tsx checkSession)
  if (isAuthenticated && !loggedInMember) {
    return <ZaniaLogoLoader />;
  }

  const userPermissions = loggedInMember
    ? getZaniaTeamMemberRoles("", loggedInMember)
    : { canAccessZaniaAskAi: false };

  // Find first accessible item
  const firstAccessible = mainSidebarItems.find((item) => {
    // Skip placeholders (e.g., Help item)
    if (item.isPlaceholder) {
      return false;
    }

    // Check permission-based access
    if (item.canAccess && !item.canAccess(userPermissions)) {
      return false;
    }

    // Check feature flag access
    if (item.featureName) {
      const accessKey = `has${item.featureName.charAt(0).toUpperCase()}${item.featureName.slice(1)}Access` as keyof typeof featureAccess;
      if (!featureAccess[accessKey]) {
        return false;
      }
    }

    return true;
  });

  // Fallback to settings if nothing accessible
  const targetPath = firstAccessible?.path || ROUTES.SETTINGS;

  return <Navigate to={`/${targetPath}`} replace />;
}
